import { Vendor } from "@shared/index";

export type SFDCAuthContextState = [state: SFDCAuthState, dispatch: SFDCAuthDispatch, utils?: UseAuthContextUtils];

export type UseAuthUtils = {
  tokenExpired: () => void;
};

// Keys used for writing to local storage
export const sfdcLocalStorageKeys = {
  sfdcAuthToken: "@sfdc_auth_token",
  sfdcIdToken: "@sfdc_id_token",
  sfdcRefreshToken: "@sfdc_refresh_token",
  sfdcIsRoasterAdmin: "@sfdc_is_roaster_admin",
  role: "@sfdc_role",
  profileName: "@sfdc_profile_name",
  contactId: "@sfdc_contact_id",
};

// Prefix applied to the `Authorization` header when sending a bearer (SFDC id) token.
export const BEARER_TOKEN_PREFIX = "Bearer ";

export type FetchTokensOnAuth = (code: string, handleSetVendorAndInitialize: HandleSetVendor) => Promise<void>;

export type HandleSetVendor = (vendor: Vendor | undefined) => void;

export type HandleSetVendorInitialized = (initialized: boolean) => void;

export type UseAuthContextUtils = {
  fetchTokensOnAuth: FetchTokensOnAuth;
  clearAuthProvider: () => void;
};

export interface SFDCAuthState {
  initialized: boolean;
  loading: boolean;
  sfdcAuthToken: string | undefined | null;
  sfdcIdToken: string | undefined | null;
  redirectUrl?: string | null;
  sfdcRefreshToken?: string | null;
  isRoasterAdmin?: boolean;
  role?: string | null;
  /**
   * The Salesforce profile name from the id token (e.g. `"admin"`,
   * `"Breville Roaster Admin Profile"`).
   *
   * Distinct from `role`: `role` is the Salesforce role, `profileName` is the
   * permission set the user logged in under, and it is `profileName` that
   * decides what the app lets them do. Kept in state — rather than read once
   * and thrown away by `isBrevilleRoasterAdmin` — because analytics reports
   * segment on it; see `useAnalyticsIdentity`.
   */
  profileName?: string | null;
  /**
   * The signed-in person's Salesforce Contact ID — the analytics `distinct_id`.
   *
   * Held here rather than read off `currentVendor` because the vendor
   * describes the account being acted on, not the actor: a Breville admin has
   * no vendor until they pick one, and the impersonation endpoint builds its
   * vendor without a contact. Sourcing identity from the vendor would leave
   * every admin and impersonated session unidentified.
   */
  contactId?: string | null;
}

export type SetInitializedAction = {
  type: "SET_INITIALIZED";
};

export type SetSFDCAuthLoadingAction = {
  type: "SET_SFDC_AUTH_LOADING";
  payload: boolean;
};

export type SetSFDCAuthTokensAction = {
  type: "SET_SFDC_AUTH_TOKENS";
  payload: {
    sfdcAuthToken: SFDCAuthState["sfdcAuthToken"];
    sfdcIdToken: SFDCAuthState["sfdcIdToken"];
    sfdcRefreshToken: SFDCAuthState["sfdcRefreshToken"];
  };
};

export type SetRoleAction = {
  type: "SET_ROLE";
  payload: SFDCAuthState["role"];
};

export type SetProfileNameAction = {
  type: "SET_PROFILE_NAME";
  payload: SFDCAuthState["profileName"];
};

export type SetContactIdAction = {
  type: "SET_CONTACT_ID";
  payload: SFDCAuthState["contactId"];
};

export type SetRedirectUrlAction = {
  type: "SET_REDIRECT_URL";
  payload: string;
};

export type ClearRedirectUrlAction = {
  type: "CLEAR_REDIRECT_URL";
};

export type SetIsRoasterAdminAction = {
  type: "SET_IS_ROASTER_ADMIN";
  payload: boolean;
};

export type SFDCAuthActions =
  | SetInitializedAction
  | SetSFDCAuthLoadingAction
  | SetSFDCAuthTokensAction
  | SetRoleAction
  | SetProfileNameAction
  | SetContactIdAction
  | SetRedirectUrlAction
  | ClearRedirectUrlAction
  | SetIsRoasterAdminAction;

export type SFDCAuthDispatch = React.Dispatch<SFDCAuthActions>;
